import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import ExerciseAutocomplete from '../components/ExerciseAutocomplete';
import { exerciseService, muscleGroupService } from '../services';
import { Edit, Trash2, Plus, Lock, Globe } from 'lucide-react';
import Swal from 'sweetalert2';

const Exercises = () => {
  const [exercises, setExercises] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'global', 'custom'
  const [formData, setFormData] = useState({
    name: '',
    muscle_group_id: '',
    description: '',
    default_weight: '',
  });

  useEffect(() => {
    loadExercises();
    loadMuscleGroups();
  }, []);

  const loadExercises = async () => {
    try {
      const response = await exerciseService.getAll();
      setExercises(response.data);
    } catch (error) {
      console.error('Error loading exercises:', error);
    }
  };

  const loadMuscleGroups = async () => {
    try {
      const response = await muscleGroupService.getAll();
      setMuscleGroups(response.data);
    } catch (error) {
      console.error('Error loading muscle groups:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingExercise) {
        await exerciseService.update(editingExercise.id, formData);
      } else {
        await exerciseService.create(formData);
      }
      setShowModal(false);
      setEditingExercise(null);
      setFormData({ name: '', muscle_group_id: '', description: '', default_weight: '' });
      loadExercises();
    } catch (error) {
      console.error('Error saving exercise:', error);
      alert('Error al guardar el ejercicio');
    }
  };

  const handleEdit = (exercise) => {
    setEditingExercise(exercise);
    setFormData({
      name: exercise.name,
      muscle_group_id: exercise.muscle_group_id,
      description: exercise.description || '',
      default_weight: exercise.default_weight || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await exerciseService.delete(id);
        loadExercises();
        Swal.fire('¡Eliminado!', 'El ejercicio ha sido eliminado.', 'success');
      } catch (error) {
        console.error('Error deleting exercise:', error);
        Swal.fire('Error', 'No se pudo eliminar el ejercicio', 'error');
      }
    }
  };

  // Filter exercises
  const filteredExercises = exercises.filter(ex => {
    // First filter by view mode
    if (viewMode === 'global' && !ex.is_global) return false;
    if (viewMode === 'custom' && ex.is_global) return false;
    
    // Then filter by muscle group
    if (selectedFilter === 'all') return true;
    return ex.muscle_group_id === parseInt(selectedFilter);
  });

  const globalExercises = filteredExercises.filter(ex => ex.is_global);
  const customExercises = filteredExercises.filter(ex => !ex.is_global);

  return (
    <Layout>
      <div className="animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-5xl font-extrabold mb-2" style={{ color: '#1e293b' }}>
              Biblioteca de Ejercicios
            </h1>
            <p className="text-gray-700 text-lg font-semibold">
              🌐 Ejercicios globales + 💪 Tus ejercicios personalizados
            </p>
          </div>
          <button
            onClick={() => {
              setEditingExercise(null);
              setFormData({ name: '', muscle_group_id: '', description: '', default_weight: '' });
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-cyan-400 to-blue-400 hover:from-cyan-500 hover:to-blue-500 text-gray-900 font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center space-x-2"
          >
            <Plus size={24} />
            <span>Nuevo Ejercicio Personalizado</span>
          </button>
        </div>

        {/* View Mode Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setViewMode('all')}
            className={`px-5 py-2.5 rounded-lg font-bold transition-all ${
              viewMode === 'all'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📚 Todos ({exercises.length})
          </button>
          <button
            onClick={() => setViewMode('global')}
            className={`px-5 py-2.5 rounded-lg font-bold transition-all ${
              viewMode === 'global'
                ? 'bg-green-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            🌐 Globales ({exercises.filter(e => e.is_global).length})
          </button>
          <button
            onClick={() => setViewMode('custom')}
            className={`px-5 py-2.5 rounded-lg font-bold transition-all ${
              viewMode === 'custom'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            💪 Personalizados ({exercises.filter(e => !e.is_global).length})
          </button>
        </div>

        {/* Filter Select */}
        <div className="mb-8">
          <label className="block text-sm font-bold text-gray-800 mb-3">
            🔍 Filtrar por grupo muscular:
          </label>
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="w-full md:w-auto px-6 py-3 bg-white border-2 border-gray-300 rounded-xl font-bold text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-md hover:border-indigo-400 transition-all cursor-pointer text-base"
          >
            <option value="all">🏋️ Todos los grupos ({filteredExercises.length})</option>
            {muscleGroups.map((group) => {
              const count = filteredExercises.filter(ex => ex.muscle_group_id === group.id).length;
              return (
                <option key={group.id} value={group.id.toString()}>
                  💪 {group.name} ({count})
                </option>
              );
            })}
          </select>
        </div>

        {/* Global Exercises Section */}
        {(viewMode === 'all' || viewMode === 'global') && globalExercises.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Globe size={28} className="mr-2 text-green-600" />
              Ejercicios Globales
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {globalExercises.map((exercise) => (
                <div key={exercise.id} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 border-2 border-green-200 hover:border-green-400 hover:shadow-2xl transition-all group">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">
                          {exercise.name}
                        </h3>
                        <Lock size={16} className="text-green-600" title="Ejercicio global (solo lectura)" />
                      </div>
                      <span className="inline-block px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg uppercase tracking-wide shadow-sm">
                        {exercise.muscle_group?.name}
                      </span>
                    </div>
                    <div className="text-4xl">🌐</div>
                  </div>
                  {exercise.description && (
                    <p className="text-sm text-gray-700 mb-4 line-clamp-2 font-medium">{exercise.description}</p>
                  )}
                  {exercise.default_weight && (
                    <div className="flex items-center space-x-2 text-sm bg-green-100 px-3 py-2 rounded-lg border-2 border-green-300">
                      <span className="text-xl">⚖️</span>
                      <span className="font-bold text-green-900">{exercise.default_weight} kg</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom Exercises Section */}
        {(viewMode === 'all' || viewMode === 'custom') && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-3xl mr-2">💪</span>
              Mis Ejercicios Personalizados
            </h2>
            {customExercises.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {customExercises.map((exercise) => (
                  <div key={exercise.id} className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-200 hover:border-purple-400 hover:shadow-2xl transition-all group">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-3 group-hover:text-purple-700 transition" style={{ color: '#1e293b' }}>
                          {exercise.name}
                        </h3>
                        <span className="inline-block px-3 py-1.5 bg-purple-600 text-white text-xs font-bold rounded-lg uppercase tracking-wide shadow-sm">
                          {exercise.muscle_group?.name}
                        </span>
                      </div>
                      <div className="text-4xl">💪</div>
                    </div>
                    {exercise.description && (
                      <p className="text-sm text-gray-700 mb-4 line-clamp-2 font-medium">{exercise.description}</p>
                    )}
                    {exercise.default_weight && (
                      <div className="flex items-center space-x-2 mb-4 text-sm bg-purple-50 px-3 py-2 rounded-lg border-2 border-purple-200">
                        <span className="text-xl">⚖️</span>
                        <span className="font-bold text-purple-900">{exercise.default_weight} kg</span>
                      </div>
                    )}
                    <div className="flex space-x-3 mt-4">
                      <button
                        onClick={() => handleEdit(exercise)}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded-lg text-sm font-bold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <Edit size={16} />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(exercise.id)}
                        className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg text-sm font-bold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <Trash2 size={16} />
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-8 text-center border-2 border-gray-200">
                <p className="text-gray-600 text-lg mb-2">📝 Aún no tienes ejercicios personalizados</p>
                <p className="text-gray-500 text-sm">Crea ejercicios personalizados para tus necesidades específicas</p>
              </div>
            )}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl transform transition-all">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  {editingExercise ? '✏️ Editar Ejercicio' : '➕ Nuevo Ejercicio'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre del ejercicio
                  </label>
                  <ExerciseAutocomplete
                    onSelect={(exercise) => {
                      setFormData({
                        ...formData,
                        name: exercise.name,
                        muscle_group_id: exercise.muscle_group_id,
                        description: exercise.description || formData.description,
                        default_weight: exercise.default_weight || formData.default_weight,
                      });
                    }}
                    placeholder="Buscar o escribir nombre del ejercicio..."
                  />
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent mt-2"
                    placeholder="O escribe el nombre manualmente"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Busca en la biblioteca global o escribe tu propio nombre
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Grupo Muscular
                  </label>
                  <select
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    value={formData.muscle_group_id}
                    onChange={(e) =>
                      setFormData({ ...formData, muscle_group_id: e.target.value })
                    }
                  >
                    <option value="">Selecciona un grupo</option>
                    {muscleGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descripción (opcional)
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows="3"
                    placeholder="Describe el ejercicio..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Peso por defecto (kg, opcional)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="20"
                    value={formData.default_weight}
                    onChange={(e) =>
                      setFormData({ ...formData, default_weight: e.target.value })
                    }
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-900 font-bold py-3 px-4 rounded-lg transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-lime-400 to-green-400 hover:from-lime-500 hover:to-green-500 text-gray-900 font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Exercises;
